import React, { useState, useEffect } from 'react';
import { getRequest, patchRequest, deleteRequest } from 'apis/ApiInstence';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import AdminTable from 'common/AdminTable';
import TruncatedMessage from 'common/TruncatedMessage';

const getImageUrl = (imagePath) => {
  if (!imagePath) return '';
  let path = imagePath.replace(/\\/g, '/'); // Normalize slashes
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path;
  }
  let baseUrl = process.env.REACT_APP_BACKEND || '';
  
  // Fix for live API server: uploads are served from the root domain
  if (baseUrl.includes('api.yashsoftsolution.com') && path.startsWith('uploads/')) {
     baseUrl = 'https://api.yashsoftsolution.com/';
  }

  if (baseUrl.endsWith('/') && path.startsWith('/')) {
    return `${baseUrl}${path.substring(1)}`;
  }
  if (!baseUrl.endsWith('/') && !path.startsWith('/')) {
    return `${baseUrl}/${path}`;
  }
  return `${baseUrl}${path}`;
};

const AllBlogs = () => {
  const [blogs, setBlogs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [currentBlog, setCurrentBlog] = useState(null);
  const [imageFiles, setImageFiles] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [updating, setUpdating] = useState(false);
  // Pagination state
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const limit = 10;

  const fetchData = async (pageNum = page) => {
  setLoading(true);
  try {
    const token = localStorage.getItem('userToken') || sessionStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };
    const blogsRes = await getRequest(`blogs?page=${pageNum}&limit=${limit}`, { headers });
    const catsRes = await getRequest('blog_category', { headers });
    
    if (blogsRes.data.success) {
      const data = blogsRes.data.data;
      const total = blogsRes.data.total || blogsRes.data.count || data.length;
      const currentPage = blogsRes.data.page || pageNum;
      const totalPagesFromApi = blogsRes.data.totalPages || Math.ceil(total / limit);
      
      setBlogs(data);
      setTotalItems(total);
      setTotalPages(totalPagesFromApi);
      setPage(currentPage);
    } else {
      toast.error(blogsRes.data.message || 'Failed to load blogs');
    }
    if (catsRes.data.success) setCategories(catsRes.data.data);
  } catch (error) {
    console.error('Error fetching data:', error);
    toast.error('Failed to load data');
  } finally {
    setLoading(false);
  }
};

  useEffect(() => {
    fetchData(1);
  }, []);

  const openEditModal = (blog) => {
    let imagesArray = [];
    try {
      imagesArray = JSON.parse(blog.images);
    } catch (e) {
      imagesArray = [];
    }
    setExistingImages(imagesArray);
    setCurrentBlog({ ...blog, category_id: blog.category_id || blog.category?.id });
    setImageFiles([]);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setCurrentBlog(null);
    setImageFiles([]);
    setExistingImages([]);
  };

  const handleBlogChange = (field, value) => {
    setCurrentBlog(prev => ({ ...prev, [field]: value }));
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    setImageFiles(prev => [...prev, ...files]);
  };

  const removeNewImage = (index) => {
    setImageFiles(prev => prev.filter((_, i) => i !== index));
  };

  const removeExistingImage = (index) => {
    setExistingImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    if (!currentBlog) return;
    setUpdating(true);
    const formData = new FormData();
    formData.append('heading', currentBlog.heading);
    formData.append('author', currentBlog.author);
    formData.append('content', currentBlog.content);
    formData.append('category_id', currentBlog.category_id);
    if (existingImages.length > 0) {
      formData.append('existing_images', JSON.stringify(existingImages));
    } else {
      formData.append('existing_images', JSON.stringify([]));
    }
    imageFiles.forEach(file => formData.append('images', file));

    try {
      const token = localStorage.getItem('userToken') || sessionStorage.getItem('userToken');
      const res = await patchRequest(`blogs/${currentBlog.id}`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });
      if (res.data.success) {
        toast.success('Blog updated successfully');
        closeModal();
        fetchData(page);
      } else {
        toast.error(res.data.message || 'Update failed');
      }
    } catch (error) {
      console.error('Error updating blog:', error);
      toast.error('Error updating blog');
    } finally {
      setUpdating(false);
    }
  };

  const handleDelete = (id) => {
    Swal.fire({
      title: 'Are you sure?',
      text: 'You will not be able to recover this blog!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!',
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const token = localStorage.getItem('userToken') || sessionStorage.getItem('userToken');
          const res = await deleteRequest(`blogs/${id}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (res.data.success) {
            toast.success('Blog deleted');
            if (blogs.length === 1 && page > 1) {
              fetchData(page - 1);
            } else {
              fetchData(page);
            }
          } else {
            toast.error(res.data.message || 'Delete failed');
          }
        } catch (error) {
          console.error('Error deleting blog:', error);
          toast.error('Error deleting blog');
        }
      }
    });
  };

  const handlePrevPage = () => {
    if (page > 1) fetchData(page - 1);
  };

  const handleNextPage = () => {
    if (page < totalPages) fetchData(page + 1);
  };

  const tableHeaders = [
    { Header: 'ID', accessor: 'id' },
    {
      Header: 'Image',
      accessor: 'images',
      Cell: ({ row }) => {
        let images = [];
        try { images = JSON.parse(row.original.images); } catch(e) { images = []; }
        const firstImage = images[0];
        return firstImage ? (
          <img src={getImageUrl(firstImage)} width={60} style={{ objectFit: 'cover' }} alt="blog" />
        ) : 'No image';
      },
    },
    { Header: 'Heading', accessor: 'heading' },
    { Header: 'Author', accessor: 'author' },
    {
      Header: 'Category',
      accessor: 'category',
      Cell: ({ row }) => row.original.category?.name || '—',
    },
    {
      Header: 'Content',
      accessor: 'content',
      Cell: ({ row }) => <TruncatedMessage text={row.original.content} maxLength={60} />,
    },
    {
      Header: 'Actions',
      accessor: 'actions',
      Cell: ({ row }) => (
        <>
          <button className="btn editbtn mx-1" onClick={() => openEditModal(row.original)}>Edit</button>
          <button className="btn removebtn mx-1" onClick={() => handleDelete(row.original.id)}>Delete</button>
        </>
      ),
    },
  ];

  if (loading) return <div className="text-center mt-5">Loading blogs...</div>;

  return (
    <div className="container-fluid mt-5">
      <div className="mainheadig mx-4 d-flex justify-content-between">
        <h4 className="font-weight-bold">All Blogs</h4>
        <button className="btn btn-success" onClick={() => window.location.href = '/blogs/add'}>+ Add Blog</button>
      </div>
      {blogs.length > 0 ? (
        <>
          <AdminTable tableHeaders={tableHeaders} tableData={blogs} />
          <div className="d-flex justify-content-between align-items-center mt-4 mx-4">
            <div>Showing {blogs.length} of {totalItems} blogs</div>
            <div>
              <button className="btn btn-secondary mx-1" onClick={handlePrevPage} disabled={page === 1}>
                Previous
              </button>
              <span className="mx-2">Page {page} of {totalPages}</span>
              <button className="btn btn-secondary mx-1" onClick={handleNextPage} disabled={page === totalPages}>
                Next
              </button>
            </div>
          </div>
        </>
      ) : (
        <div className="text-center mt-4">No blogs found.</div>
      )}

      {/* Edit Modal */}
      {showModal && currentBlog && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Edit Blog</h5>
                <button type="button" className="close" onClick={closeModal}>&times;</button>
              </div>
              <div className="modal-body">
                <div className="form-group">
                  <label htmlFor="editHeading">Heading</label>
                  <input
                    id="editHeading"
                    type="text"
                    className="form-control"
                    value={currentBlog.heading}
                    onChange={e => handleBlogChange('heading', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="editAuthor">Author</label>
                  <input
                    id="editAuthor"
                    type="text"
                    className="form-control"
                    value={currentBlog.author}
                    onChange={e => handleBlogChange('author', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="editCategory">Category</label>
                  <select
                    id="editCategory"
                    className="form-control"
                    value={currentBlog.category_id || ''}
                    onChange={e => handleBlogChange('category_id', parseInt(e.target.value))}
                  >
                    <option value="">Select category</option>
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="editContent">Content</label>
                  <textarea
                    id="editContent"
                    className="form-control"
                    rows="5"
                    value={currentBlog.content}
                    onChange={e => handleBlogChange('content', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="existingImages">Existing Images</label>
                  <div id="existingImages" className="d-flex flex-wrap">
                    {existingImages.map((img, idx) => (
                      <div key={idx} className="position-relative m-1">
                        <img
                          src={getImageUrl(img)}
                          width={80}
                          height={80}
                          style={{ objectFit: 'cover' }}
                          alt="existing"
                        />
                        <button
                          type="button"
                          className="btn btn-sm btn-danger position-absolute"
                          style={{ top: 0, right: 0 }}
                          onClick={() => removeExistingImage(idx)}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="form-group">
                  <label htmlFor="newImages">Add New Images</label>
                  <input
                    id="newImages"
                    type="file"
                    multiple
                    accept="image/*"
                    className="form-control-file"
                    onChange={handleImageChange}
                  />
                  {imageFiles.length > 0 && (
                    <div className="d-flex flex-wrap mt-2">
                      {imageFiles.map((file, idx) => (
                        <div key={idx} className="position-relative m-1">
                          <img
                            src={URL.createObjectURL(file)}
                            width={80}
                            height={80}
                            style={{ objectFit: 'cover' }}
                            alt="preview"
                          />
                          <button
                            type="button"
                            className="btn btn-sm btn-danger position-absolute"
                            style={{ top: 0, right: 0 }}
                            onClick={() => removeNewImage(idx)}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={closeModal}>Cancel</button>
                <button className="btn btn-primary" onClick={handleSave} disabled={updating}>
                  {updating ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AllBlogs;